import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  DefaultButton,
  Icon,
  IconButton,
  Label,
  PrimaryButton,
  Stack,
  Text,
  TextField,
} from '@fluentui/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { fileToBase64, formatDate } from '@moko/utils';
import { TenantSelectorField, pageTenants, type GetTenantsFn } from '@moko/ui';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  duplicateReportTemplate,
  updateReportTemplate,
  uploadReportTemplate,
  type IReportTemplate,
} from '../../services/reportTemplateService';
import AppContext from '../AppContext';

export type ReportTemplateEditorMode = 'new' | 'edit' | 'duplicate';

interface Props {
  mode: ReportTemplateEditorMode;
}

interface ReportTemplateForm {
  name: string;
  tenantId: number | null;
  /** Holds the existing/selected file name; drives the mandatory-file rule. */
  fileName: string;
}

const ACCEPTED_FILE_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const HOW_IT_WORKS_STEPS = [
  'Download a Word template from the associated form.',
  'Customize the template externally in Microsoft Word.',
  'Upload the completed template back to this report definition.',
  'Preview generated reports with actual data.',
];

const requiredLabel = (text: string) => <Label required>{text}</Label>;

const ReportTemplateEditor = ({ mode }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const context = useContext(AppContext);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const profile = useAtomValue(profileDataAtom);

  const template = (location.state as { template?: IReportTemplate } | null)
    ?.template;

  const isNew = mode === 'new';
  const isEdit = mode === 'edit';
  const isDuplicate = mode === 'duplicate';

  // Tenant is only editable when duplicating (copy to a different tenant).
  // The file can be provided on New (required) or replaced on Edit (optional).
  // Duplicate copies the source file server-side, so no file input there.
  const fileEditable = isNew || isEdit;

  // New uses the user's current tenant; edit/duplicate seed from the source.
  const initialTenantId = isNew
    ? profile.currentTenant || null
    : template?.tenantId
    ? Number(template.tenantId)
    : null;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const tenants = profile.tenants;

  const { control, handleSubmit, getValues, setValue } =
    useForm<ReportTemplateForm>({
      mode: 'onChange',
      defaultValues: {
        name: template?.name ?? '',
        tenantId: initialTenantId,
        fileName: template?.fileName ?? '',
      },
    });

  const watchedName = useWatch({ control, name: 'name' });
  const watchedTenantId = useWatch({ control, name: 'tenantId' });
  const watchedFileName = useWatch({ control, name: 'fileName' });

  const getTenants: GetTenantsFn = useCallback(
    async (_ac, pageSize, pageNo, _orderBy, search) =>
      pageTenants(
        tenants.map((tenant) => ({ id: tenant.id, name: tenant.name })),
        pageSize,
        pageNo,
        search
      ),
    [tenants]
  );

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.type !== ACCEPTED_FILE_TYPE && !file.name.endsWith('.docx')) {
      setErrorMessage('Only .docx files are allowed.');
      return;
    }
    setSelectedFile(file);
    setValue('fileName', file.name, { shouldValidate: true });
    // Prefill the name from the file only when the user hasn't named it yet.
    if (!getValues('name')?.trim()) {
      setValue('name', file.name, { shouldValidate: true });
    }
  };

  // CTA is enabled per the confirmed rules:
  // - New: name + file required
  // - Edit: name required (file optional)
  // - Duplicate: name/tenant optional, but at least one must be provided
  const isValid = isDuplicate
    ? !!watchedName?.trim() || watchedTenantId != null
    : !!watchedName?.trim() && (!isNew || !!watchedFileName);

  const onSubmit = async (values: ReportTemplateForm) => {
    if (submitting) return;
    setSubmitting(true);
    const abortController = new AbortController();
    try {
      if (isNew) {
        if (!selectedFile) return;
        const content = await fileToBase64(selectedFile);
        await uploadReportTemplate(abortController, {
          content,
          name: values.name,
          fileName: selectedFile.name,
          contentType: ACCEPTED_FILE_TYPE,
          entityType: template?.entityType || 'Work',
        });
        setSuccessMessage(`Report template "${values.name}" created.`);
      } else if (isEdit) {
        if (!template) return;
        const content = selectedFile
          ? await fileToBase64(selectedFile)
          : undefined;
        await updateReportTemplate(abortController, template.id, {
          name: values.name,
          ...(content
            ? {
                content,
                fileName: selectedFile?.name,
                contentType: ACCEPTED_FILE_TYPE,
                entityType: template.entityType || 'Work',
              }
            : {}),
        });
        setSuccessMessage(`Report template "${values.name}" updated.`);
      } else {
        if (!template) return;
        await duplicateReportTemplate(abortController, template.id, {
          ...(values.name?.trim() ? { name: values.name.trim() } : {}),
          ...(values.tenantId != null ? { tenantId: values.tenantId } : {}),
        });
        setSuccessMessage('Report template duplicated.');
      }
      navigate('/reportTemplates');
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to save report template.');
    } finally {
      setSubmitting(false);
    }
  };

  const heading = isNew
    ? 'New report template'
    : isDuplicate
    ? 'Duplicate report template'
    : `Report template ${watchedName ?? ''}`.trim();

  const ctaText = isNew ? 'Create' : isDuplicate ? 'Duplicate' : 'Save';

  // Drive the single page title from the heading (updates live for Edit).
  useEffect(() => {
    context.setSelectedTab(heading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heading]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: 24,
          padding: '8px 4px',
        }}
      >
        <div
          style={{
            flex: '1 1 360px',
            maxWidth: 560,
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            padding: 20,
            backgroundColor: '#fff',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx"
            style={{ display: 'none' }}
            onChange={handleFileSelected}
          />

          <Stack tokens={{ childrenGap: 16 }}>
            <Controller
              control={control}
              name="tenantId"
              render={({ field }) => (
                <TenantSelectorField
                  getTenants={getTenants}
                  tenantId={field.value ?? undefined}
                  tenantName={
                    tenants.find((tenant) => tenant.id === field.value)?.name
                  }
                  disabled={!isDuplicate}
                  onSelected={(tenant) => field.onChange(tenant.id)}
                />
              )}
            />

            <Controller
              control={control}
              name="name"
              rules={
                isDuplicate
                  ? {}
                  : {
                      required: 'Name is required',
                      validate: (v) => v.trim() !== '' || 'Name is required',
                    }
              }
              render={({ field, fieldState }) => (
                <Stack tokens={{ childrenGap: 4 }}>
                  {isDuplicate ? <Label>Name</Label> : requiredLabel('Name')}
                  <TextField
                    value={field.value}
                    onChange={(_, v) => field.onChange(v ?? '')}
                    onBlur={field.onBlur}
                    placeholder="Enter a report name"
                  />
                  {fieldState.error && (
                    <Text variant="small" style={{ color: 'red' }}>
                      {fieldState.error.message}
                    </Text>
                  )}
                </Stack>
              )}
            />

            <Controller
              control={control}
              name="fileName"
              rules={isNew ? { required: 'A template file is required' } : {}}
              render={({ field, fieldState }) => (
                <Stack tokens={{ childrenGap: 4 }}>
                  {isNew ? (
                    requiredLabel('Template file')
                  ) : (
                    <Label>Template file</Label>
                  )}
                  {field.value ? (
                    <Stack
                      horizontal
                      verticalAlign="center"
                      horizontalAlign="space-between"
                      tokens={{ childrenGap: 8 }}
                      style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 4,
                        padding: '8px 12px',
                      }}
                    >
                      <Stack
                        horizontal
                        verticalAlign="center"
                        tokens={{ childrenGap: 8 }}
                      >
                        <Icon iconName="WordDocument" />
                        <Stack>
                          <Text>{field.value}</Text>
                          {template && !selectedFile && (
                            <Text variant="small" style={{ color: '#666' }}>
                              {template.createdAt
                                ? formatDate(template.createdAt, true)
                                : ''}
                            </Text>
                          )}
                        </Stack>
                      </Stack>
                      {/* Replace is available where the file is editable (New/Edit).
                        Duplicate copies the source file server-side, so it's
                        display-only. The file is mandatory, hence no remove. */}
                      {fileEditable && (
                        <IconButton
                          iconProps={{ iconName: 'Refresh' }}
                          title="Replace file"
                          ariaLabel="Replace file"
                          onClick={handlePickFile}
                        />
                      )}
                    </Stack>
                  ) : (
                    fileEditable && (
                      <DefaultButton
                        iconProps={{ iconName: 'Upload' }}
                        text="Upload .docx file"
                        onClick={handlePickFile}
                      />
                    )
                  )}
                  {fieldState.error && (
                    <Text variant="small" style={{ color: 'red' }}>
                      {fieldState.error.message}
                    </Text>
                  )}
                </Stack>
              )}
            />

            <Stack
              horizontal
              tokens={{ childrenGap: 12 }}
              style={{ marginTop: 8 }}
            >
              <DefaultButton
                text="Cancel"
                onClick={() => navigate('/reportTemplates')}
              />
              <PrimaryButton
                type="submit"
                text={ctaText}
                disabled={!isValid || submitting}
              />
            </Stack>
          </Stack>
        </div>

        <aside
          style={{
            flex: '0 1 340px',
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            padding: 20,
            backgroundColor: '#fff',
          }}
        >
          <Text
            variant="mediumPlus"
            style={{
              fontWeight: 600,
              color: '#2b6cb0',
              display: 'block',
              marginBottom: 16,
            }}
          >
            How report templates work
          </Text>
          <Stack tokens={{ childrenGap: 16 }}>
            {HOW_IT_WORKS_STEPS.map((step, index) => (
              <Stack
                key={index}
                horizontal
                verticalAlign="start"
                tokens={{ childrenGap: 12 }}
              >
                <span
                  style={{
                    flex: '0 0 auto',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: '#2b6cb0',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {index + 1}
                </span>
                <Text style={{ color: '#4a5568' }}>{step}</Text>
              </Stack>
            ))}
          </Stack>
        </aside>
      </div>
    </form>
  );
};

export default ReportTemplateEditor;
